"use client";

import type { UserBoughtEventGuessItem } from "@/api/comments";
import { SelectionIndicator } from "@/components/ui/selection-indicator";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { SmartImage } from "@/components/ui/smart-image";
import { VisuallyHidden } from "@/components/ui/visually-hidden";
import { getBetTypeDisplay } from "@/constants/bet-type-style";
import { useThemeMode } from "@/features/theme/theme-provider";
import { getBrandLogoIcon, getBrandLogoIconDark } from "@/lib/brand-config";
import { cn } from "@/lib/utils";
import { calc, formatUnitPrice, get } from "@/utils/utils";
import { ArrowLeft } from "lucide-react";

interface PositionSelectDrawerProps {
    open: boolean;
    onOpenChange: ( open: boolean ) => void;
    eventImageUrl?: string;
    eventTitle: string;
    options: UserBoughtEventGuessItem[];
    selectedOption: UserBoughtEventGuessItem | null;
    onSelect: ( option: UserBoughtEventGuessItem ) => void;
    matchups?: any[];
}

function formatProfitLoss( option: UserBoughtEventGuessItem ) {
    const nowChance = get( option, "nowChancePercentage", 0 );
    const userChance = get( option, "userChancePercentage", 0 );
    // Difference in cents, rounded to 2 decimal places
    const diffCents = calc( "nowChance - userChance", { nowChance, userChance } );
    const diffCentsRounded = Number( Number( diffCents ).toFixed( 2 ) );
    // Percentage change: (nowChance - userChance) / userChance * 100, rounded to 2 decimal places
    const percentageChange = userChance !== 0
        ? Number( calc( "(diff / user) * 100", { diff: diffCentsRounded, user: userChance } ) ).toFixed( 2 )
        : "";
    const diffSign = diffCentsRounded >= 0 ? "+" : "";
    const percentageDisplay = userChance !== 0 ? `${diffSign}${percentageChange}%` : "--%";
    return `${diffSign}${formatUnitPrice( diffCentsRounded )}(${percentageDisplay})`;
}

export default function PositionSelectDrawer(
    { open, onOpenChange, eventImageUrl, eventTitle, options, selectedOption, onSelect, matchups }:
        PositionSelectDrawerProps,
) {
    const handleSelect = ( option: UserBoughtEventGuessItem ) => {
        onSelect( option );
        onOpenChange( false );
    };
    const { themeMode } = useThemeMode();
    const fallbackCoverImage = themeMode === "dark" ? getBrandLogoIconDark() : getBrandLogoIcon();
    const coverImage = eventImageUrl || fallbackCoverImage;

    return (
        <Sheet open={open} onOpenChange={onOpenChange}>
            <SheetContent
                side="bottom"
                showCloseButton={false}
                className="rounded-t-2xl px-4 pt-3 pb-8 h-[80vh] overflow-y-auto gap-0"
            >
                <VisuallyHidden>
                    <SheetTitle>Select Position</SheetTitle>
                </VisuallyHidden>

                {/* Header with back button */}
                <div className="flex items-center gap-3 h-[52px]">
                    <button onClick={() => onOpenChange( false )} className="p-1 -ml-1">
                        <ArrowLeft className="size-6 text-t1" />
                    </button>
                </div>

                {/* Event info */}
                <div className="flex items-center gap-3 mb-3">
                    <div className="size-12 rounded-lg overflow-hidden bg-surface-subtle shrink-0">
                        <SmartImage
                            src={coverImage}
                            alt={eventTitle}
                            className="w-full h-full object-cover"
                            imageUrlOptions={{ width: 150, height: 150 }}
                        />
                    </div>
                    <h2 className="text-base font-medium text-t1 leading-snug line-clamp-2">{eventTitle}</h2>
                </div>

                {/* Options list */}
                <div className="flex flex-col">
                    {options.map( ( option ) => {
                        const isSelected = selectedOption
                            && selectedOption.guessNo === option.guessNo
                            && selectedOption.betType === option.betType;
                        const betTypeDisplay = getBetTypeDisplay( option.betType, matchups );

                        return (
                            <button
                                key={`${option.guessNo}-${option.betType}`}
                                onClick={() => handleSelect( option )}
                                className={cn(
                                    "w-full flex items-center justify-between gap-3 py-4 border-b border-stroke transition-colors",
                                    "hover:bg-surface-subtle",
                                )}
                            >
                                <div className="flex items-center gap-3 min-w-0">
                                    <SelectionIndicator selected={!!isSelected} className="shrink-0" />
                                    <span
                                        className={cn(
                                            "text-sm font-medium truncate",
                                            betTypeDisplay.isNeutral ? "text-t1" : betTypeDisplay.textClass,
                                        )}
                                    >
                                        {betTypeDisplay.label}
                                        {option.guessTitle && (
                                            <span className="text-t1 font-medium">· {option.guessTitle}</span>
                                        )}
                                    </span>
                                </div>
                                <span className="text-sm text-t1 font-medium shrink-0">
                                    {formatProfitLoss( option )}
                                </span>
                            </button>
                        );
                    } )}
                </div>
            </SheetContent>
        </Sheet>
    );
}
